/**
 * Utility functions for handling opening hours and status
 */

export interface OpeningHourEntry {
    weekday: number; // 0-6: Monday-Sunday
    open_time: string | null; // format: HH:mm:ss
    close_time: string | null; // format: HH:mm:ss
    is_closed: boolean;
}

/**
 * Get current opening status based on structured opening hours
 * @param openingHours Structured opening hours array
 * @param targetDay Optional specific day to check (0=Monday, 6=Sunday). If not provided, uses current time.
 * @param tempClosed Info about temporary closure
 * @returns Object with status string and color class
 */
export function getOpeningStatus(
    openingHours: OpeningHourEntry[],
    targetDay?: number,
    tempClosed?: {
        isClosed: boolean;
        until?: string | Date | null;
        reason?: string | null;
    },
    i18n?: {
        locale?: string;
        t?: (key: string, params?: Record<string, string | number>) => string;
    }
): {
    status: string;
    isOpen: boolean;
    colorClass: string;
    nextAction?: string;
} {
    const t = i18n?.t;
    const locale = i18n?.locale || "en";
    const translate = (key: string, fallback: string, params?: Record<string, string | number>): string => {
        const raw = t ? t(key, params) : fallback;
        // If t() returns key when missing, fall back to provided text.
        return raw === key ? fallback : raw;
    };
    const dayNameForWeekday = (weekday: number): string => {
        const keyMap = [
            "shops.filters.day.monday",
            "shops.filters.day.tuesday",
            "shops.filters.day.wednesday",
            "shops.filters.day.thursday",
            "shops.filters.day.friday",
            "shops.filters.day.saturday",
            "shops.filters.day.sunday",
        ];
        const fallbackMap = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
        if (weekday < 0 || weekday > 6) return fallbackMap[0];
        return translate(keyMap[weekday], fallbackMap[weekday]);
    };

    if (tempClosed?.isClosed) {
        let status = translate("openingHours.tempClosed", "Temporarily closed");
        if (tempClosed.reason) status += `: ${tempClosed.reason}`;
        if (tempClosed.until) {
            const date = new Date(tempClosed.until);
            const dateStr = date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
            status += ` (${translate("openingHours.untilDate", "until")} ${dateStr})`;
        }
        return {
            status,
            isOpen: false,
            colorClass: "text-red-600 font-bold"
        };
    }

    if (!openingHours || openingHours.length === 0) {
        return {
            status: translate("openingHours.unknown", "Opening hours unknown"),
            isOpen: false,
            colorClass: "text-muted-foreground"
        };
    }

    const now = new Date();
    const jsDay = now.getDay();
    const currentSchemaDay = jsDay === 0 ? 6 : jsDay - 1;

    // Use targetDay if provided, otherwise use current day
    const schemaDay = targetDay !== undefined ? targetDay : currentSchemaDay;
    const isToday = targetDay === undefined || targetDay === currentSchemaDay;

    // Get all open periods for the target day, sorted by time
    const todayEntries = openingHours
        .filter(h => h.weekday === schemaDay && !h.is_closed && h.open_time && h.close_time)
        .sort((a, b) => (a.open_time || "").localeCompare(b.open_time || ""));

    // If no open periods for the target day
    if (todayEntries.length === 0) {
        if (!isToday) {
            return {
                status: translate("openingHours.closedOnSelectedDay", "Closed on this day"),
                isOpen: false,
                colorClass: "text-red-500 font-medium"
            };
        }

        // Find when it opens again (for today/now view)
        return findNextOpening(openingHours, schemaDay, translate, dayNameForWeekday);
    }

    // If it's a future/past day, just show the concatenated hours
    if (!isToday) {
        const hoursStr = todayEntries
            .map(e => `${e.open_time!.slice(0, 5)}-${e.close_time!.slice(0, 5)}`)
            .join(" & ");
        return {
            status: `${translate("openingHours.openLabel", "Open")}: ${hoursStr}`,
            isOpen: true,
            colorClass: "text-green-600 font-medium"
        };
    }

    // Logic for "now" (isToday === true)
    const currentTime = now.getHours() * 100 + now.getMinutes();

    // 1. Check if we ARE currently in an open period
    const currentPeriod = todayEntries.find(e => {
        const open = timeToInt(e.open_time!);
        const close = timeToInt(e.close_time!);
        return currentTime >= open && currentTime < close;
    });

    if (currentPeriod) {
        const closeTimeParts = currentPeriod.close_time!.split(':');
        const minutesToClose = (parseInt(closeTimeParts[0]) * 60 + parseInt(closeTimeParts[1])) - (now.getHours() * 60 + now.getMinutes());

        if (minutesToClose > 0 && minutesToClose <= 30) {
            return {
                status: `${translate("openingHours.openUntil", "Open until")} ${currentPeriod.close_time!.slice(0, 5)} (${translate("openingHours.closingSoon", "closing soon")})`,
                isOpen: true,
                colorClass: "text-orange-500 font-medium"
            };
        }

        return {
            status: `${translate("openingHours.openUntil", "Open until")} ${currentPeriod.close_time!.slice(0, 5)}`,
            isOpen: true,
            colorClass: "text-green-600 font-medium"
        };
    }

    // 2. Not currently open. Check if there's a LATER period today (e.g., after lunch break)
    const nextPeriodToday = todayEntries.find(e => timeToInt(e.open_time!) > currentTime);

    if (nextPeriodToday) {
        return {
            status: `${translate("openingHours.closedNow", "Closed now")}, ${translate("openingHours.reopensAt", "reopens at")} ${nextPeriodToday.open_time!.slice(0, 5)}`,
            isOpen: false,
            colorClass: "text-red-500 font-medium"
        };
    }

    // 3. After all periods for today, find the next day they are open
    return findNextOpening(openingHours, schemaDay, translate, dayNameForWeekday);
}

/**
 * Helper to convert HH:mm:ss to integer HHmm
 */
function timeToInt(timeStr: string): number {
    const parts = timeStr.split(':');
    return parseInt(parts[0]) * 100 + parseInt(parts[1]);
}

/**
 * Helper to find the next day/time a shop opens
 */
function findNextOpening(
    openingHours: OpeningHourEntry[],
    schemaDayAt: number,
    translate: (key: string, fallback: string, params?: Record<string, string | number>) => string = (_key, fallback) => fallback,
    dayNameForWeekday: (weekday: number) => string = () => "day"
) {
    let nextOpenDay: OpeningHourEntry | undefined;
    for (let i = 1; i <= 7; i++) {
        const nextDayIdx = (schemaDayAt + i) % 7;
        // Find the FIRST period for that next day
        const entries = openingHours
            .filter(h => h.weekday === nextDayIdx && !h.is_closed && h.open_time)
            .sort((a, b) => (a.open_time || "").localeCompare(b.open_time || ""));

        if (entries.length > 0) {
            nextOpenDay = entries[0];
            break;
        }
    }

    let status = translate("openingHours.closedNow", "Closed now");
    if (nextOpenDay) {
        const isTomorrow = (schemaDayAt + 1) % 7 === nextOpenDay.weekday;
        const time = nextOpenDay.open_time?.slice(0, 5) || "00:00";
        const dayLabel = isTomorrow
            ? translate("openingHours.tomorrow", "tomorrow")
            : dayNameForWeekday(nextOpenDay.weekday);
        status = `${translate("openingHours.closedNow", "Closed now")}, ${translate("openingHours.opensLabel", "open")} ${dayLabel} ${translate("openingHours.atLabel", "at")} ${time}`;
    }

    return {
        status,
        isOpen: false,
        colorClass: "text-red-500 font-medium"
    };
}
